// Eskom PARCC — RTM finding phrasebook (Phase 2).
//
// Seeds the criteria / observation / risk / recommendation prose for a finding
// from its criterion wording, so the auto-draft reads like Kettle's document
// (rtm.txt 4.1/4.2). Keyed by a keyword in the criterion text; a generic fallback
// covers everything else. The consultant edits every field in the composer, so
// this is a starting draft, never the last word. Pure.

export interface Phrase {
  criteria: string;
  observation: string;
  risk: string;
  recommendation: string;
  /** People / Process / System and Plant / Other → Annexure B secondary. */
  rootCausePrimary: string;
  rootCauseSecondary: string;
}

interface PhraseRule {
  match: RegExp;
  phrase: Phrase;
}

const PEOPLE_DISCIPLINE = { rootCausePrimary: 'People', rootCauseSecondary: 'Lack of Discipline' };

const RULES: PhraseRule[] = [
  {
    match: /non-disclosure|\bnda\b/i,
    phrase: {
      criteria:
        'In terms of Eskom’s PSCM Procedure Rev 5-1034 and Treasury Regulation 16A, non-disclosure agreements (NDAs) must be signed by evaluation team members before they take part in the procurement process.',
      observation:
        'It was noted that non-disclosure agreements from the evaluation team members were not signed prior to taking part in the procurement process as prescribed by Eskom’s procurement policy Rev 5.',
      risk:
        'This exposes Eskom to confidentiality breaches, with employees not formally and legally bound to confidentiality at the outset, creating a risk that sensitive pricing or supplier information is shared outside the procurement process.',
      recommendation:
        'Managers should ensure that all non-disclosure agreements have been sought and are kept on OpenText before the commencement of the procurement process.',
      ...PEOPLE_DISCIPLINE,
    },
  },
  {
    match: /declaration of interest|conflict of interest|\bdoi\b|\bcoi\b/i,
    phrase: {
      criteria:
        'Eskom’s procurement policy requires that declarations of interest from evaluating team members are obtained at the inception of the procurement process.',
      observation:
        'Declarations of interest from evaluating team members were not obtained at the inception of the procurement process, as required by Eskom’s procurement policy.',
      risk:
        'Without a declaration in place there is no formal mechanism to recuse conflicted evaluators, increasing the risk that supplier selection decisions are influenced by undisclosed personal, financial or business relationships.',
      recommendation:
        'Ensure evaluations and negotiations do not commence before all declarations of interest of the evaluators are received and filed on OpenText.',
      ...PEOPLE_DISCIPLINE,
    },
  },
  {
    match: /\bcsd\b|central supplier database|tax compliance/i,
    phrase: {
      criteria:
        'The procurement process requires that a CSD report is generated for suppliers at mandate to negotiate, at evaluation, and at feedback report where an award is granted (PFMA Section 38(1)(a)(iii)).',
      observation:
        'It was determined that procurement officers are not complying with the requirement to verify supplier compliance on the Central Supplier Database (CSD) at each stage of the procurement process.',
      risk:
        'Failure to verify compliance at each stage increases the risk of awarding contracts to suppliers who are not tax compliant, or who may have been restricted or deregistered during the procurement process.',
      recommendation:
        'A supervisory review should confirm that CSD reports are obtained and supplier tax compliance verified at mandate, evaluation and feedback stages before award.',
      ...PEOPLE_DISCIPLINE,
    },
  },
  {
    match: /opentext|edms|uploaded|document management|audit trail|filed/i,
    phrase: {
      criteria:
        'In terms of PSCM Rev 5-1043 (8.2 Establishing and maintaining an audit trail), original physical documents must be filed, legibly scanned and uploaded into the prescribed taxonomy on the Eskom EDMS (OpenText) before a file is transferred to the documentation centre.',
      observation:
        'Several mandatory procurement documents were unavailable or had not been uploaded to OpenText at the time of review.',
      risk:
        'Reliance on offline storage may be deemed insufficient for compliance with record-keeping requirements, and irregular expenditure may not be detected promptly as supporting evidence is not readily accessible for review.',
      recommendation:
        'Procurement officers should upload all supporting documents onto OpenText in real time, and managers should perform monthly checks to confirm all files are uploaded.',
      ...PEOPLE_DISCIPLINE,
    },
  },
  {
    match: /budget letter/i,
    phrase: {
      criteria:
        'Eskom’s procurement policy requires an approved budget letter prior to the placement of a task order where the procurement strategy identifies the need based on the risk of the transaction.',
      observation:
        'The requirement to obtain an approved budget letter prior to the placement of a task order was transgressed.',
      risk:
        'Placing a task order without a confirmed budget letter increases the risk of unauthorised or irregular expenditure.',
      recommendation:
        'A confirmed budget letter should be placed on file before a task order is placed.',
      ...PEOPLE_DISCIPLINE,
    },
  },
  {
    match: /bbbee|b-bbee|preferential procurement|pppfa/i,
    phrase: {
      criteria:
        'In terms of the Preferential Procurement Policy Framework Act (PPPFA) and Eskom’s procurement policy Rev 5, Broad-Based Black Economic Empowerment certificates must be obtained from suppliers participating in the formal tendering process.',
      observation:
        'Procurement officers were unable to present B-BBEE certificates for suppliers participating in the formal tendering process upon request during this review.',
      risk:
        'The absence of B-BBEE certificates undermines preferential procurement compliance and exposes Eskom to audit findings.',
      recommendation:
        'All mandatory statutory documents, including B-BBEE certificates, should be obtained from suppliers and placed on file before awarding the contract.',
      ...PEOPLE_DISCIPLINE,
    },
  },
  {
    match: /cross functional team|cft|appointment letter/i,
    phrase: {
      criteria:
        'Eskom’s procurement policy requires that Cross Functional Teams (CFT) are formally appointed in writing (32-1034).',
      observation:
        'It was noted that CFT appointment letters were not fully signed and supported on file.',
      risk:
        'Without a formally appointed CFT, accountability for the evaluation is unclear and the integrity of the process is weakened.',
      recommendation:
        'Ensure Cross Functional Teams are formally appointed in writing and the signed appointment letters are filed before evaluation begins.',
      ...PEOPLE_DISCIPLINE,
    },
  },
  {
    match: /task order|ownership|accountability/i,
    phrase: {
      criteria:
        'The task order procurement process requires clear ownership and a complete, retrievable set of supporting documents per PSCM Rev 5.',
      observation:
        'Supporting documents relating to task orders were either retained by end users outside of the procurement function or could not be attributed to any responsible party.',
      risk:
        'The absence of ownership and accountability over the task order process undermines the ability to conduct meaningful compliance reviews and maintain complete procurement records.',
      recommendation:
        'Assign clear ownership for the task order population and ensure all supporting documents are held within the procurement function on OpenText.',
      ...PEOPLE_DISCIPLINE,
    },
  },
];

const GENERIC: Phrase = {
  criteria:
    'In terms of Eskom’s PSCM Procedure Rev 5 and applicable Treasury regulations, complete, accurate and reliable procurement records must be maintained for the transaction.',
  observation:
    'During the review of the files the criterion was not met — the required document or control could not be evidenced.',
  risk:
    'Failure to maintain complete and properly filed procurement documentation exposes Eskom to non-compliance with internal policies and legislative requirements, increasing the risk of irregular expenditure and audit findings.',
  recommendation:
    'Management should reinforce compliance with procurement policies and ensure the required documentation is obtained and filed before the process is concluded.',
  ...PEOPLE_DISCIPLINE,
};

/** The best-matching phrase for a criterion string (generic fallback). */
export function phraseFor(criterion: string): Phrase {
  const c = String(criterion ?? '');
  for (const rule of RULES) if (rule.match.test(c)) return rule.phrase;
  return GENERIC;
}
